import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Col,
  Select,
  Row,
  message,
  Modal,
  Table,
  Spin,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { QRCode as AntdQRCode } from "antd";

import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { fetchPincode } from "../../components/pincode/FetchPincode";
import { fetchStates } from "../../components/state/FetchStates";
import { fetchCities } from "../../components/city/FetchCities";
import { fetchCountries } from "../../components/country/FetchCountries";
import { token } from "../../constants/Constants";
import { createQr, getAllQr, getQRdetail, getQrLocationById } from "../../endpoints/API";
import LocationDetail from "./LocationDetail";

const QRCode = () => {
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);

  const [form] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pincode, setPincode] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [qrValue, setQrValue] = useState("antd");
  const [qrList, setQrList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedQRLocationDetail, setSelectedQRLocationDetail] = useState(null);
  const [selectedLocationData, setSelectedLocationData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { Option } = Select;

  // Load dropdown data
  useEffect(() => {
    const fetchAll = async () => {
      try {
        await Promise.all([
          fetchPincode(setPincode, baseUrl, prodTestUrl),
          fetchStates(setStates, baseUrl, prodTestUrl),
          fetchCities(setCities, baseUrl, prodTestUrl),
          fetchCountries(setCountries, baseUrl, prodTestUrl),
        ]);
        await fetchQRList();
      } catch {
        message.error("Failed to fetch initial data.");
      }
    };
    fetchAll();
  }, []);

  const fetchQRList = async () => {
    setLoading(true);
    try {
      const response = await getAllQr();

      if (!response.statusText == "OK") throw new Error("Failed to fetch QR list");

      const data = response?.data;
      console.log(data);
      setQrList(data);
    } catch (error) {
      console.error(error);
      message.error("Error loading QR data.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setDetailLoading(true);
    try {
      const response = await getQrLocationById(id);
      setSelectedQRLocationDetail(response.data.locations);
    } catch (error) {
      message.error("Failed to load QR details");
      console.error(error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownloadQRCode = (link, name) => {
    const canvas = document.createElement("canvas");

    const qrCode = <AntdQRCode value={link} size={200} />;
    const container = document.createElement("div");

    import("react-dom").then((ReactDOM) => {
      ReactDOM.render(qrCode, container);

      setTimeout(() => {
        const qrCanvas = container.querySelector("canvas");
        if (qrCanvas) {
          const image = qrCanvas.toDataURL("image/png");
          const a = document.createElement("a");
          a.href = image;
          a.download = `${name}_qr.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          message.error("QR code rendering failed.");
        }
      }, 500);
    });
  };

  const columns = [
    {
      title: "Location ID",
      dataIndex: "location",
      key: "location_id",
      width: 110,
      render: (location) => location?.id ?? "N/A",
    },
    {
      title: "Shop Name",
      dataIndex: "shop_name",
      key: "shop_name",
      width: 160,
    },
    {
      title: "QR Link",
      dataIndex: "qr_link",
      key: "qr_link",
      width: 220,
      render: (text) => (
        <a href={text} target="_blank" rel="noopener noreferrer"
          style={{ wordBreak: "break-all" }}>
          {text}
        </a>
      ),
    },
    {
      title: "Show Details",
      key: "details",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleViewDetails(record.id)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const showModal = () => setIsModalOpen(true);
  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setQrValue("antd");
  };

  const handleOnFinish = async (values) => {
    try {
      const qrContent = values.address_identifier;
      setQrValue(qrContent);

      const payload = {
        ...values,
        qr_data: qrContent,
      };
      console.log(payload);
      const response = await createQr(payload);

      if (!response.statusText == "OK") throw new Error("Failed to save QR");

      message.success("QR Code saved successfully!");
    } catch (error) {
      console.error(error);
      message.error("An error occurred while saving QR.");
    }
  };

  const handleLocationClick = async (locationId, dates = []) => {
    let start_date = "";
    let end_date = "";

    if (dates?.length === 2) {
      start_date = dayjs(dates[0]).format("YYYY-MM-DD");
      end_date = dayjs(dates[1]).format("YYYY-MM-DD");
    }

    try {
      setDetailLoading(true);
      const response = await getQRdetail(locationId, start_date, end_date);
      setSelectedLocationData(response.data);
    } catch (err) {
      console.error(err);
      setSelectedLocationData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .qr-table-wrapper .ant-table {
            font-size: 12px !important;
          }
          .qr-table-wrapper .ant-table-cell {
            padding: 6px 8px !important;
            white-space: nowrap;
          }
          .qr-table-wrapper .ant-btn {
            font-size: 12px !important;
          }
          .qr-modal .ant-modal-body {
            padding: 12px !important;
          }
          .qr-modal .ant-form-item {
            margin-bottom: 10px !important;
          }
          .qr-modal .ant-col-12 {
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      {selectedQRLocationDetail ? (
        <LocationDetail
          selectedQRLocationDetail={selectedQRLocationDetail}
          setSelectedQRLocationDetail={setSelectedQRLocationDetail}
          detailLoading={detailLoading}
          handleLocationClick={handleLocationClick}
          selectedLocationData={selectedLocationData}
          setSelectedLocationData={setSelectedLocationData}
        />
      ) : (
        <>
          <div className="flex justify-end mb-4">
            <Button type="primary" onClick={showModal}>
              Generate QR
            </Button>
          </div>

          <div className="qr-table-wrapper" style={{ overflowX: "auto", width: "100%" }}>
            <Table
              columns={columns}
              dataSource={qrList}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 5, responsive: true, size: "small" }}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </div>
        </>
      )}

      <Modal
        title="Generate QR Code"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        className="qr-modal"
        width="min(600px, 95vw)"
        style={{ maxWidth: "95vw" }}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleOnFinish}
          initialValues={{ country: undefined }}
        >
          <Form.Item
            label="Shop Name"
            name="address_identifier"
            rules={[{ required: true, message: "Please enter address label" }]}
          >
            <Input placeholder="Enter address label" />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Country"
                name="country"
                rules={[{ required: true, message: "Please select country" }]}
              >
                <Select placeholder="Select country" style={{ width: "100%" }}>
                  {countries.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="State"
                name="state"
                rules={[{ required: true, message: "Please select state" }]}
              >
                <Select placeholder="Select state" style={{ width: "100%" }}>
                  {states.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="City"
                name="city"
                rules={[{ required: true, message: "Please select city" }]}
              >
                <Select placeholder="Select city" style={{ width: "100%" }}>
                  {cities.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Pincode"
                name="pincode"
                rules={[{ required: true, message: "Please select pincode" }]}
              >
                <Select placeholder="Select pincode" style={{ width: "100%" }}>
                  {pincode.map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.pin}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Area"
                name="apartment_road_area"
                rules={[{ required: true, message: "Please enter area" }]}
              >
                <Input placeholder="Enter area" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="House/Flat"
                name="house_flat_floor_no"
                rules={[{ required: true, message: "Please enter house/flat" }]}
              >
                <Input placeholder="Enter house/flat" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ textAlign: "center", marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default QRCode;